import type { TensorflowModel } from '@/ml/tflite';

import { TirvuFace, type DetectedFace, type Tile } from '@/ml/tirvu-face';

// Specs do FaceAntiSpoofing.tflite (Deep Tree Learning, CVPR 2019):
//   Input  : 256x256x3 RGB, pixel/255 (normalizado [0,1])
//   Outputs: dois tensores de 8 floats — clss_pred e leaf_node_mask
//   Score  : Σ clss_pred[i] * leaf_node_mask[i]  →  quanto MAIOR, mais ataque
//   Decisão: score > ATTACK_THRESHOLD = spoof
// Fonte: github.com/syaringan357/Android-MobileFaceNet-MTCNN-FaceAntiSpoofing
const MODEL_INPUT_SIZE = 256;
const OUTPUT_SIZE = 8;

/**
 * ⚠️ RECALIBRE ESTE VALOR ANTES DE SUBIR PARA PRODUÇÃO.
 *
 * O 0,2 herdado foi ajustado contra o recorte ANTIGO, que era a caixa do rosto +40%
 * ESTICADA para 256x256 (a caixa é retangular, o tile é quadrado). Agora o recorte é
 * quadrado e sem deformação — a imagem que o modelo vê mudou, e com ela a
 * distribuição do score. Não dá para assumir que o mesmo corte separa igual.
 *
 * Como calibrar: colete o `score` de ~30 rostos reais e ~30 ataques (foto impressa,
 * foto na tela de outro celular) com o build novo, e escolha o ponto que separa os
 * dois grupos. O `score` já vai no toast de erro e no console.
 */
/**
 * ── Limiar de ataque: 0,90, medido em aparelho ──────────────────────────────
 *
 * O 0,2 herdado do projeto de referência **reprova rosto vivo em massa**. Medido
 * no TirvuTab em 30/08/2026 (Galaxy S22 / Android 16), com o MESMO modelo e o
 * MESMO recorte desta constante (256 px, caixa com 40% de folga, sem alinhamento):
 *
 *   rosto vivo · 17 tentativas de 4 pessoas · −0,006 a 0,895
 *   ataque     ·  3 tentativas               ·  0,897 a 0,995  (foto em tela)
 *
 * Em 0,2, **7 das 17 tentativas vivas (41%)** ficavam acima do limiar. Numa das
 * pessoas o score fica em 0,85–0,90 sempre: em qualquer limiar que barre ataque,
 * ela nunca bate ponto. O score depende da PESSOA, não de ela estar viva.
 *
 * Em 0,90, nos mesmos dados: **0 recusas falsas em 17** e 2 dos 3 ataques barrados.
 * Prioriza não barrar quem é de verdade, ao custo de deixar passar o ataque menos
 * evidente. **Não é uma barreira de segurança — é um detector de tentativa
 * grosseira.**
 *
 * ⚠️ A margem é de 0,005 (o rosto vivo mais alto deu 0,895). Se alguém voltar a ser
 * barrado em campo, o conserto NÃO é subir o limiar — a 0,99 dois dos três ataques
 * passam. É trocar o modelo.
 *
 * ⚠️ A medição veio do TirvuTab. O modelo e o recorte são os mesmos, mas a câmera e
 * a distância de uso aqui são outras (celular na mão × tablet de parede). Vale
 * recolher a distribuição neste app antes de tratar o 0,90 como definitivo.
 *
 * ⚠️ E o limiar sozinho não basta: dois frames quase idênticos do mesmo rosto vivo
 * deram −0,005 e 0,323. Decida sobre MAIS DE UM frame (mediana), nunca sobre um.
 */
const ATTACK_THRESHOLD = 0.9;

/**
 * ── Limiar de RECUSA: 0,99, e só por unanimidade ────────────────────────────
 *
 * O `ATTACK_THRESHOLD` acima continua sendo o ponto de MEDIÇÃO — é ele que
 * classifica a amostra no log e alimenta a recalibração. Este aqui é outra coisa:
 * o ponto a partir do qual o cadastro RECUSA alguém.
 *
 * Separar os dois foi a resposta do TirvuTab ao mesmo problema, e a escada de lá
 * vale como histórico: 0,90 barrava gente; separaram medição de recusa e subiram
 * para 0,99 exigindo unanimidade; **continuou barrando colaborador legítimo**.
 *
 * O motivo é o vão: entre o rosto vivo mais alto medido (0,895) e o ataque mais
 * baixo (0,897) há **0,002**. Com um vão desse tamanho não existe limiar que barre
 * ataque sem barrar gente.
 */
const REFUSE_THRESHOLD = 0.99;

/**
 * ── A prova de vida está DESLIGADA no cadastro (08/09/2026) ─────────────────
 *
 * Desligada, não removida: ela continua rodando e continua no log. O que sai é só
 * o poder de RECUSAR — o score segue medido a cada cadastro e vai para a linha de
 * diagnóstico, e é dele que sairá a recalibração quando houver dado de campo.
 *
 * Por que agora: usuários relatavam "tela preta" no cadastro — que é esta recusa,
 * porque o estado de erro DESMONTA a câmera e o que sobra é o fundo preto com o
 * oval vermelho. Acontecia em Android e iOS, e só no cadastro: no ponto a prova de
 * vida já vinha desligada (`antiSpoof: false` em `screens/Camera.tsx` e
 * `screens/CameraDiaria.tsx`). Era esse o degrau que faltava explicar por que o
 * ponto funcionava e o cadastro não.
 *
 * O TirvuTab chegou aqui antes, pelo mesmo caminho e com os mesmos dois limiares
 * tentados — ver `PROVA_DE_VIDA_ATIVA` em `tirvu-app-tab/tirvutab/services/FaceAntiSpoof.ts`.
 *
 * ⚠️ O que se perde: quase nada que funcionasse. A porta pegava o ataque GROSSEIRO
 * e só. E ela guardava uma porta que já estava aberta: como a VERIFICAÇÃO do ponto
 * não roda anti-spoof, quem levantar uma foto na frente da câmera bate ponto do
 * mesmo jeito, desde que exista referência. O que continua no lugar é o portão de
 * qualidade estrito do cadastro (nitidez, brilho, interocular, ângulos — ver
 * `avaliarQualidade` em `services/FaceRecognition.ts`), que é mais exigente do que
 * o do coletor, e a foto anexada à batida.
 *
 * ⚠️ Custo de deixar barrando: no cliente configurado para registrar SOMENTE com
 * biometria, não conseguir cadastrar é não conseguir bater ponto. Recusar quem é de
 * verdade é dano diário e certo; a foto na tela é risco ocasional.
 *
 * Para religar: `PROVA_DE_VIDA_ATIVA = true`. Antes disso, junte os scores do log
 * de gente real E de uma tentativa com foto NOS APARELHOS DA FROTA — a calibração
 * de 0,90 saiu de um Galaxy S22 e não sobreviveu ao primeiro aparelho diferente.
 */
const PROVA_DE_VIDA_ATIVA = false;

/** Spec do tile deste modelo. O orquestrador pede junto com os demais, numa passada só. */
export const SPEC_ANTISPOOF = {
  size: MODEL_INPUT_SIZE,
  mode: 'box' as const,
  // 40% de folga: diferente do reconhecimento, aqui queremos ver o ENTORNO — moiré da
  // tela, borda do papel, moldura do aparelho. É onde mora a evidência da fraude, e é
  // por isso que este tile NÃO é alinhado por landmarks (alinhar cortaria justamente
  // essa região).
  padding: 0.4,
};

let modelPromise: Promise<TensorflowModel | null> | null = null;

const loadModel = (): Promise<TensorflowModel | null> => {
  if (!modelPromise) {
    modelPromise = (async () => {
      try {
        const { loadTensorflowModel } = await import('@/ml/tflite');
        return await loadTensorflowModel("/models/anti_spoof.tflite" /* WEB: modelo servido pelo PWA */);
      } catch {
        return null;
      }
    })();
  }
  return modelPromise;
};

const buildModelInput = (rgb: Uint8Array): Float32Array => {
  const total = MODEL_INPUT_SIZE * MODEL_INPUT_SIZE * 3;
  if (rgb.length !== total) {
    throw new Error(`Tile anti-spoof com ${rgb.length} bytes, esperado ${total}`);
  }
  const input = new Float32Array(total);
  for (let i = 0; i < total; i++) input[i] = rgb[i] / 255;
  return input;
};

export const FaceAntiSpoof = {
  /** Pré-carrega o modelo. Chamar no warmup junto com FaceRecognition. */
  warmup: async (): Promise<void> => {
    await loadModel();
  },

  /**
   * Analisa um tile 256x256 já recortado pelo `tirvu-face`.
   *
   * @returns `{ real, score }`. `score` é a probabilidade de ATAQUE (alto = fraude).
   */
  isRealFromTile: async (tile: Tile): Promise<{ real: boolean; score: number }> => {
    const model = await loadModel();
    if (!model) throw new Error('Módulo TFLite indisponível (requer dev build)');

    const input = buildModelInput(tile.data);

    // Defesa: o fast-tflite só valida shape/dtype em build DEBUG; em release um
    // mismatch de buffer pode estourar nativamente (não capturável). Validamos no
    // JS p/ degradar com Error capturável em vez de fechar o app.
    const inT: any = (model as any).inputs?.[0];
    if (inT?.dataType && inT.dataType !== 'float32') {
      throw new Error(`anti_spoof.tflite dtype ${inT.dataType} (esperado float32)`);
    }
    const inElems = Array.isArray(inT?.shape)
      ? inT.shape.reduce((a: number, n: number) => a * (n || 1), 1)
      : 0;
    if (inElems && inElems !== input.length) {
      throw new Error(`anti_spoof.tflite espera ${inElems} elems, recebeu ${input.length}`);
    }

    const outputs = await model.run([input]);

    // Casa as saídas por NOME (clss_pred / leaf_node_mask) quando disponível, com
    // fallback p/ a ordem 0/1 — assim, se o conversor inverter a ordem dos tensores,
    // o score continua correto (senão liberaria spoof silenciosamente).
    const outInfos: any[] = (model as any).outputs ?? [];
    const idxByName = (needle: string, fallback: number) => {
      const i = outInfos.findIndex((o) => String(o?.name ?? '').toLowerCase().includes(needle));
      return i >= 0 ? i : fallback;
    };
    const classPred = outputs[idxByName('clss', 0)] as Float32Array;
    const leafMask = outputs[idxByName('leaf', 1)] as Float32Array;

    if (!classPred || !leafMask || classPred.length < OUTPUT_SIZE || leafMask.length < OUTPUT_SIZE) {
      throw new Error(
        `Output anti-spoof inesperado (clss=${classPred?.length}, leaf=${leafMask?.length})`,
      );
    }

    let score = 0;
    for (let i = 0; i < OUTPUT_SIZE; i++) score += classPred[i] * leafMask[i];

    return { real: score <= ATTACK_THRESHOLD, score };
  },

  /** Atalho quando não há tile pronto: detecta, recorta e analisa numa chamada. */
  isReal: async (
    imageUri: string,
    face?: DetectedFace,
  ): Promise<{ real: boolean; score: number }> => {
    let alvo = face;
    if (!alvo) {
      const res = await TirvuFace.detect(imageUri);
      alvo = res.faces[0];
      if (!alvo?.landmarks) throw new Error('Nenhum rosto identificado na foto');
    }
    const [tile] = await TirvuFace.extractTiles(imageUri, alvo, [SPEC_ANTISPOOF]);
    return FaceAntiSpoof.isRealFromTile(tile);
  },

  CONFIG: {
    INPUT_SIZE: MODEL_INPUT_SIZE,
    /** Ponto de MEDIÇÃO — classifica a amostra no log. Não recusa ninguém. */
    ATTACK_THRESHOLD,
    /** Ponto de RECUSA — só vale com `PROVA_DE_VIDA_ATIVA` e por unanimidade. */
    REFUSE_THRESHOLD,
    PROVA_DE_VIDA_ATIVA,
  },
};
